import React, { useState } from "react";
import { ArrowDown, ArrowUp, GripVertical, Plus, Trash2 } from "lucide-react";
import PaginatedSelect from "../../components/ui/PaginatedSelect";
import ActionButton from "../../components/ui/ActionButton";
import ParameterDialog from "../itemRouting/ParameterDialog";
import RoutingOperationFields from "./RoutingOperationFields";
import OperationParameters from "../itemRouting/OperationParameters";

const OperationMappingFields = ({ mapping, ordered = false, disabled = false }) => {
  const { options, value, onChange, isLoading, error, retry } = mapping;
  const [parameterOperationId, setParameterOperationId] = useState(null);
  const isDisabled = disabled || isLoading || Boolean(error);
  const move = (index, offset) => {
    if (ordered) {
      mapping.moveRow(index, offset);
      return;
    }
    const next = [...value];
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    onChange(next);
  };
  const rowOptions = (id, index) => {
    const selectedElsewhere = new Set(value.filter((_, rowIndex) => rowIndex !== index));
    const available = options.filter((option) => !selectedElsewhere.has(option.value));
    return id && !available.some((option) => option.value === id)
      ? [...available, { value: id, label: id }]
      : available;
  };

  return (
    <div className="space-y-3 border-t border-slate-100 pt-4">
      <p className="text-sm text-slate-500">
        {ordered ? "Select a relation type for each routing operation." : "Operations match the machine relation type. Changing the relation type clears the operation selections."}
      </p>
      {mapping.hasInvalidOperations && !isLoading && <p role="alert" className="text-sm text-red-600">Some mapped operations do not match this machine's relation type. Replace or remove them before saving.</p>}
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2">
        <h2 className="text-sm font-semibold text-slate-900">{ordered ? "Item Routing" : "Operations"}</h2>
        <ActionButton
          action="add"
          icon={Plus}
          label="Add Operation"
          type="button"
          onClick={() => onChange([...value, ""])}
          disabled={isDisabled || (!ordered && !mapping.relationType)}
        />
      </div>
      <div className="space-y-2">
        {value.map((id, index) => (
          <div
            key={mapping.rows[index].key}
            className="grid gap-2 rounded-md border border-slate-100 bg-white px-3 py-3 sm:grid-cols-[40px_minmax(0,1fr)_auto] sm:items-center"
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-500">
              {ordered && <GripVertical className="h-4 w-4 text-slate-300" />}
              <span>{index + 1}</span>
            </div>
            {ordered ? <RoutingOperationFields
              plantId={mapping.plantId}
              row={mapping.rows[index]}
              index={index}
              selectedOperationIds={value}
              onOperationChange={(operationId) => mapping.changeRow(mapping.rows[index].key, { operation_id: operationId })}
              onRelationChange={(relationType) => mapping.changeRow(mapping.rows[index].key, { relation_type: relationType, operation_id: "" })}
              disabled={isDisabled}
            /> : <label>
              <span className="sr-only">Operation {index + 1}</span>
              <PaginatedSelect
                value={id}
                onChange={(operationId) => onChange(value.map((current, rowIndex) => rowIndex === index ? operationId : current))}
                options={rowOptions(id, index)}
                disabled={isDisabled || (!ordered && !mapping.relationType)}
                placeholder="Select operation"
              />
            </label>}
            <div className="flex items-center justify-end gap-1.5">
              {mapping.supportsParameters && (
                <ActionButton
                  action="add"
                  label="Add Parameter"
                  size="sm"
                  variant="outline"
                  disabled={isDisabled || !id}
                  onClick={() => setParameterOperationId(id)}
                />
              )}
              {ordered && <>
                <ActionButton action="previous" icon={ArrowUp} iconOnly size="sm" type="button" title="Move up" aria-label={`Move routing operation ${index + 1} up`} disabled={isDisabled || index === 0} onClick={() => move(index, -1)} />
                <ActionButton action="next" icon={ArrowDown} iconOnly size="sm" type="button" title="Move down" aria-label={`Move routing operation ${index + 1} down`} disabled={isDisabled || index === value.length - 1} onClick={() => move(index, 1)} />
              </>}
              <ActionButton action="delete" icon={Trash2} iconOnly size="sm" type="button" title="Remove" aria-label={`Remove ${ordered ? "routing " : ""}operation ${index + 1}`} disabled={isDisabled} onClick={() => ordered ? mapping.removeRow(mapping.rows[index].key) : onChange(value.filter((_, rowIndex) => rowIndex !== index))} />
            </div>
            {mapping.supportsParameters && <div className="sm:col-span-3">
              <OperationParameters
                plantId={mapping.plantId}
                itemId={mapping.itemId}
                operationId={id}
                drafts={mapping.parameters.filter((parameter) => parameter.operation_id === id)}
                onRemove={mapping.removeParameter}
                disabled={isDisabled}
              />
            </div>}
          </div>
        ))}
      </div>
      {error ? (
        <div role="alert" className="text-sm text-red-600">
          {error}
          <ActionButton action="refresh" type="button" label="Retry loading mappings" onClick={retry} disabled={disabled} />
        </div>
      ) : isLoading ? (
        <p className="text-sm text-slate-500">Loading operations...</p>
      ) : !ordered && !mapping.relationType ? (
        <p className="text-sm text-slate-500">Select a relation type first.</p>
      ) : !ordered && options.length === 0 ? (
        <p className="text-sm text-slate-500">No operations found for this relation type and plant.</p>
      ) : value.some((id) => !id) ? (
        <p className="text-sm text-slate-500">Select an operation for every mapping row, or remove the empty row before saving.</p>
      ) : value.length === 0 ? (
        <p className="text-sm text-slate-500">Click Add Operation to add a mapping.</p>
      ) : null}
      {mapping.supportsParameters && parameterOperationId && value.includes(parameterOperationId) && (
        <ParameterDialog
          operationLabel={options.find((option) => option.value === parameterOperationId)?.label || parameterOperationId}
          saveTarget="item"
          onClose={() => setParameterOperationId(null)}
          onAdd={(parameter) => {
            mapping.addParameter(parameterOperationId, parameter);
            setParameterOperationId(null);
          }}
        />
      )}
    </div>
  );
};

export default OperationMappingFields;
